import { watchlist } from '@api/api/watchlist';
import {
  FAVORITE_WATCHLIST,
  useRemoteStorage,
} from '@terminal/hooks/use-remote-storage';
import { QUERY_KEY_WATCHLIST_SUMMARY } from '@terminal/react-query-hooks/watchlist/useWatchlistSummary';
import { queryClient } from '@terminal/services/query-client/queryClient';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import { create } from '@terminal/utils/store/createStore';

import { getListEntities } from './getListEntities';
import {
  getValidWatchlistIds,
  reconcileWatchlistLinkedStorage,
} from './reconcile-watchlist-remote-storage';
import {
  addListIdToMap,
  deleteListIdFromMap,
  getUniqueListEntities,
  hasFavoriteWatchlist,
  mapAndSortWatchlists,
  mapListsWithNewListEntities,
  mapListsWithNewListName,
  mapListsWithUpdatedWatchlist,
  sortWatchlistsByRecency,
} from './useWatchlist.utils';
import {
  LegacyWatchlists,
  List,
  SubscribedWatchlists,
  Watchlist,
  WatchlistEntity,
  WatchlistStore,
  Watchlists,
} from './watchlist-types';

const handleFavorite = (id: string) => {
  const items = useRemoteStorage.getState().items;
  return id === items?.[FAVORITE_WATCHLIST];
};

const defaultFilterGroups = {
  ASSET_FILTERS: {
    activeFilterOptions: [],
    activeFilters: [],
    allFilters: [],
  },
};

export const useWatchlist = create<WatchlistStore>((set, get) => ({
  filterGroups: defaultFilterGroups,
  activeListId: undefined,
  activeModal: undefined,
  setActiveModal: ({ activeModal }) => set({ activeModal }),
  lists: [],
  error: undefined,
  listIdsByTag: new Map(),
  getLists: async () => {
    const isWatchlistNotificationsEnabled = useFlags
      .getState()
      .getFeatureFlagState('PLATFORM-4949-watchlist-notifications');
    const res = isWatchlistNotificationsEnabled
      ? await apiFetch<SubscribedWatchlists>(watchlist.getLists.path, {
          schema: watchlist.getLists.schema,
        })
      : await apiFetch<LegacyWatchlists>(watchlist.getLegacyLists.path, {
          schema: watchlist.getLegacyLists.schema,
        });

    if (res.error) {
      set({ error: res.error });
    } else {
      const mappedWatchlists = mapAndSortWatchlists(res.data).filter(
        watchList => watchList.name
      );

      set({
        error: undefined,
        lists: mappedWatchlists,
        activeListId: mappedWatchlists[0]?.id,
      });

      await reconcileWatchlistLinkedStorage(
        getValidWatchlistIds(mappedWatchlists)
      );
    }

    if (!res.error) {
      // important! need to wait until we get all the list to prevent race conditions
      const promises = res.data.map(list => get().getList(list.id));
      await Promise.all(promises);
    }
  },
  getList: async (id: string) => {
    const entities = await getListEntities(id);

    set(state => {
      const listIdsByTag = new Map(state.listIdsByTag);
      const lists = state.lists.map(list => ({
        ...list,
        entities: list.id === id ? entities : list.entities,
      }));

      lists.forEach(list => {
        list.entities.forEach(entity => {
          addListIdToMap({ listIdsByTag, listId: list.id, tag: entity.tag });
        });
      });

      return { lists, listIdsByTag };
    });
  },
  setListSubscription: async (id, subscribed) => {
    const currentList = get().lists.find(list => list.id === id);
    if (!currentList || currentList.subscribed === subscribed) {
      return;
    }

    const previousSubscribed = currentList.subscribed;

    set(state => ({
      lists: state.lists.map(list =>
        list.id === id ? { ...list, subscribed } : list
      ),
    }));

    const res = await apiFetch(watchlist.setListSubscription.path(id), {
      method: 'put',
      body: { subscribed },
    });

    if (res.error) {
      set(state => ({
        lists: state.lists.map(list =>
          list.id === id ? { ...list, subscribed: previousSubscribed } : list
        ),
      }));
    }
  },

  toggleFavoriteWatchlist: async id => {
    const storeItems = useRemoteStorage.getState().storeItems;
    const isFavoriteAlready = handleFavorite(id);

    if (isFavoriteAlready) {
      await storeItems({ [FAVORITE_WATCHLIST]: '' });
      set(state => ({
        lists: state.lists.map(list => ({
          ...list,
          isFavorite: false,
        })),
      }));
      return;
    }

    await storeItems({ [FAVORITE_WATCHLIST]: id });
    set(state => ({
      lists: state.lists.map(list => ({
        ...list,
        isFavorite: list.id === id,
      })),
      activeListId: id,
    }));
  },
  createListLoading: false,
  createList: async (name, tags, unresolvedTags = []) => {
    set({ createListLoading: true });
    const entities = tags.map(tag => ({ tag }));
    const res = await apiFetch<Watchlist>(watchlist.postList.path, {
      method: 'post',
      handledErrorCodes: [httpCode.forbidden],
      body: {
        name,
        entities,
        unresolved_entities: unresolvedTags,
      },
    });

    if (res.error) {
      set({ error: res.error });
      set({ createListLoading: false });
      return null;
    }

    set(state => {
      const createdList = {
        ...res.data,
        entities,
        subscribed: true,
        last_update: res.data.last_update || new Date().toISOString(),
      };
      const newLists = sortWatchlistsByRecency([...state.lists, createdList]);
      const listIdsByTag = new Map(state.listIdsByTag);
      entities.forEach(entity => {
        addListIdToMap({
          listIdsByTag,
          listId: res.data.id,
          tag: entity.tag,
        });
      });

      const hasFavorite = hasFavoriteWatchlist();
      return {
        lists: newLists,
        listIdsByTag,
        activeListId:
          hasFavorite && state.activeListId ? state.activeListId : res.data.id,
      };
    });

    set({ createListLoading: false });
    return res.data.id;
  },
  updateListName: async (id, name) => {
    const res = await apiFetch<List>(watchlist.putList.path(id), {
      method: 'put',
      body: {
        name,
      },
    });
    set(({ lists }) =>
      res.error
        ? { error: res.error }
        : {
            lists: mapListsWithNewListName({ lists, id, name }),
          }
    );
    return res;
  },
  updateList: async ({ id, name, tags }) => {
    const entities = tags.map(tag => ({ tag }));
    const res = await apiFetch<List>(watchlist.putList.path(id), {
      method: 'put',
      body: {
        name,
        entities,
      },
    });

    if (res.error) {
      set({ error: res.error });
    } else {
      queryClient.removeQueries({
        queryKey: [QUERY_KEY_WATCHLIST_SUMMARY, id],
      });

      const previousTags =
        get()
          .lists.find(list => list.id === id)
          ?.entities.map(entity => entity.tag) ?? [];
      const removedTags = previousTags.filter(tag => !tags.includes(tag));
      const listIdsByTag = new Map(get().listIdsByTag);
      removedTags.forEach(tag =>
        deleteListIdFromMap({ listIdsByTag, tag, listId: id })
      );
      tags.forEach(tag => addListIdToMap({ listIdsByTag, listId: id, tag }));

      set(({ lists }) => ({
        lists: mapListsWithUpdatedWatchlist({
          lists,
          id,
          name,
          entities,
        }),
        listIdsByTag,
      }));
    }

    return res;
  },
  deleteList: async id => {
    const res = await apiFetch(watchlist.deleteList.path(id), {
      method: 'delete',
    });

    if (res.error) {
      set({ error: res.error });
      return;
    }

    const listIdsByTag = new Map(get().listIdsByTag);
    const tagsToRemove = get()
      .lists.find(list => list.id === id)
      ?.entities.map(e => e.tag);

    tagsToRemove?.forEach(tag => {
      deleteListIdFromMap({ listIdsByTag, tag, listId: id });
    });

    set(({ activeListId, lists }) => {
      const newLists = lists.filter(list => list.id !== id);
      return {
        error: undefined,
        lists: newLists,
        listIdsByTag,
        activeListId: id === activeListId ? newLists?.[0]?.id : activeListId,
      };
    });

    await reconcileWatchlistLinkedStorage(getValidWatchlistIds(get().lists));
  },
  addItemToList: async (id, tag) => {
    const res = await apiFetch<WatchlistEntity>(
      watchlist.postListEntities.path(id),
      {
        method: 'post',
        body: {
          tag,
          quantity: 0,
        },
      }
    );

    queryClient.removeQueries({
      queryKey: [QUERY_KEY_WATCHLIST_SUMMARY, id],
    });

    const listIdsByTag = new Map(get().listIdsByTag);
    addListIdToMap({ listIdsByTag, listId: id, tag });

    set(({ lists }) => {
      if (res.error) {
        return { error: res.error };
      }

      const updatedAt = new Date().toISOString();
      const listsWithNewEntity = mapListsWithNewListEntities({
        lists,
        id,
        entities: res.data,
      });
      const listsWithUpdatedTimestamp = listsWithNewEntity.map(list =>
        list.id === id ? { ...list, last_update: updatedAt } : list
      );

      return {
        lists: sortWatchlistsByRecency(listsWithUpdatedTimestamp),
        listIdsByTag,
      };
    });
  },
  removeItemFromList: async (id, tag) => {
    const res = await apiFetch(watchlist.deleteListEntities.path(id, tag), {
      method: 'delete',
    });

    if (res.error) {
      set({ error: res.error });
      return;
    }

    queryClient.removeQueries({
      queryKey: [QUERY_KEY_WATCHLIST_SUMMARY, id],
    });

    set(state => {
      const newLists = state.lists.reduce<Watchlist[]>((accumulator, list) => {
        if (list.id !== id) {
          return [...accumulator, list];
        }

        if (list.entities.length === 1) {
          return accumulator;
        }

        accumulator.push({
          ...list,
          entities: list.entities.filter(entity => entity.tag !== tag),
        });
        return accumulator;
      }, []);

      const listIdsByTag = new Map(state.listIdsByTag);
      deleteListIdFromMap({ listIdsByTag, tag, listId: id });

      let activeListId = state.activeListId;

      if (id === state.activeListId && newLists.length < state.lists.length) {
        activeListId = newLists?.[0]?.id;
      }

      return { lists: newLists, listIdsByTag, activeListId };
    });
  },
  isInAnyWatchlists: async (tag: string) => {
    const res = await apiFetch<Watchlists>(
      watchlist.getListsByEntities.path(tag)
    );

    if (res.error) {
      return [];
    }

    return res.data;
  },
  /** Use backend as source of truth  */
  isInWatchlist: async (tag: string, watchlistId: string) => {
    const res = await apiFetch<Watchlists>(
      watchlist.getListsByEntities.path(tag)
    );

    if (res.error) {
      return false;
    }

    return res.data.some(res => res.id === watchlistId);
  },
  createListByAsset: async (assetTag: string, newListName: string) => {
    const { createList } = get();
    const listId = await createList(newListName, [assetTag]);

    if (listId) {
      set({ activeListId: listId });
    }

    return listId;
  },
  editListByAsset: async (id: string, assetTag: string) => {
    const { addItemToList, lists } = get();
    const [uniqueAssets, duplicateTags] = getUniqueListEntities({
      lists,
      id,
      tags: [assetTag],
    });
    const resolved = await Promise.all(
      uniqueAssets.map(tag => addItemToList(id, tag))
    );
    return [resolved.length ? uniqueAssets : [], duplicateTags];
  },

  hasListWithName: (name: string) =>
    get().lists.some(list => list.name === name),
  setActiveListId: (id?: string) => set({ activeListId: id }),
  resetFilterGroups: () => set({ filterGroups: defaultFilterGroups }),
}));
